import { Button } from '../../../components/ui/button'
import { PexIcon } from '../../../components/icons/PexIcon'
import type { SellerStoresStrings } from '../../../i18n/seller/stores'
import { ProviderMark } from './ProviderMark'
import type {
  StoreProviderDefinition,
  StoreProviderId,
} from './store-providers'

type ComparisonProviderId = Extract<
  StoreProviderId,
  'shopify' | 'etsy' | 'amazon'
>

export function PlatformComparisonSection({
  providers,
  copy,
  onConnect,
}: {
  providers: readonly StoreProviderDefinition[]
  copy: SellerStoresStrings
  onConnect: (provider: StoreProviderDefinition) => void
}) {
  return (
    <section aria-labelledby="platform-comparison-heading">
      <h2
        id="platform-comparison-heading"
        className="text-xl font-semibold tracking-tight text-slate-950 appearance-dark:text-[var(--color-text)]"
      >
        {copy.page.noStore}
      </h2>
      <p className="mt-1 text-sm text-slate-600 appearance-dark:text-[var(--color-text-muted)]">
        {copy.page.noStoreDescription}
      </p>

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        {providers.map((provider) => {
          const guide = copy.guide[provider.id as ComparisonProviderId]
          return (
            <article
              key={provider.id}
              className="flex min-h-80 flex-col rounded-xl border border-slate-200 appearance-dark:border-[var(--color-border)] bg-white appearance-dark:bg-[var(--color-surface)] p-5 shadow-[var(--shadow-card)]"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <ProviderMark provider={provider} size="sm" />
                  <h3 className="text-lg font-semibold text-slate-950 appearance-dark:text-[var(--color-text)]">
                    {provider.name}
                  </h3>
                </div>
                {provider.recommended ? (
                  <span className="rounded-full bg-indigo-50 appearance-dark:bg-[var(--color-surface-muted)] px-2.5 py-1 text-[10px] font-semibold text-indigo-700 appearance-dark:text-[var(--color-info)] ring-1 ring-indigo-200 appearance-dark:ring-[var(--color-border)]">
                    {copy.status.recommended}
                  </span>
                ) : null}
              </div>

              <p className="mt-4 text-sm leading-6 text-slate-600 appearance-dark:text-[var(--color-text-muted)]">
                {guide.description}
              </p>
              <p className="mt-4 text-xs font-semibold text-slate-800 appearance-dark:text-[var(--color-text)]">
                {copy.labels.highlights}:
              </p>
              <ul className="mt-2 space-y-1.5 text-sm text-slate-600 appearance-dark:text-[var(--color-text-muted)]">
                {guide.highlights.map((highlight) => (
                  <li key={highlight} className="flex gap-2">
                    <PexIcon
                      name="success"
                      size="xs"
                      tone="success"
                      className="mt-0.5"
                    />
                    <span>{highlight}</span>
                  </li>
                ))}
              </ul>

              <dl className="mt-5 grid grid-cols-3 gap-2 border-t border-slate-100 appearance-dark:border-[var(--color-border)] pt-4 text-xs">
                <div>
                  <dt className="text-slate-400 appearance-dark:text-[var(--color-text-muted)]">{copy.labels.setup}</dt>
                  <dd className="mt-1 font-medium text-slate-700 appearance-dark:text-[var(--color-text)]">{guide.setup}</dd>
                </div>
                <div>
                  <dt className="text-slate-400 appearance-dark:text-[var(--color-text-muted)]">{copy.labels.cost}</dt>
                  <dd className="mt-1 font-medium text-slate-700 appearance-dark:text-[var(--color-text)]">{guide.cost}</dd>
                </div>
                <div>
                  <dt className="text-slate-400 appearance-dark:text-[var(--color-text-muted)]">{copy.labels.subscription}</dt>
                  <dd className="mt-1 font-medium text-slate-700 appearance-dark:text-[var(--color-text)]">
                    {guide.subscription}
                  </dd>
                </div>
              </dl>

              <div className="mt-auto flex items-center justify-between pt-5">
                <span className="text-sm font-medium text-indigo-700 appearance-dark:text-[var(--color-info)]">
                  {copy.actions.learnMore}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onConnect(provider)}
                >
                  {copy.actions.connect}
                  <PexIcon
                    name="caretRight"
                    size="xs"
                    tone="interactive"
                    className="ml-1"
                  />
                </Button>
              </div>
            </article>
          )
        })}
      </div>
    </section>
  )
}
